import React, { useState } from "react";
import {
  Plus,
  FolderOpen,
  Save,
  Settings,
  Activity,
  ChevronDown,
  FileText,
  Trash2,
} from "lucide-react";
import clsx from "clsx";
import toast from "react-hot-toast";

import { useProjectStore } from "../stores/projectStore";

const Header = ({ onNewProject, onToggleExecution, hasRunningJobs, onLogout }) => {
  const [showProjectMenu, setShowProjectMenu] = useState(false);

  const { currentProject, projects, loadProject, saveProject, deleteProject } =
    useProjectStore();

  // プロジェクト切り替え
  const handleSwitchProject = (projectId) => {
    loadProject(projectId);
    setShowProjectMenu(false);
    toast.success("プロジェクトを切り替えました");
  };

  // プロジェクト削除
  const handleDeleteProject = (projectId, projectName) => {
    if (window.confirm(`プロジェクト "${projectName}" を削除しますか？`)) {
      deleteProject(projectId);
      setShowProjectMenu(false);
      toast.success("プロジェクトを削除しました");
    }
  };

  // プロジェクト保存
  const handleSave = () => {
    saveProject();
    toast.success("プロジェクトを保存しました");
  };

  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6">
      {/* 左側: ロゴとプロジェクト */}
      <div className="flex items-center space-x-4">
        {/* ロゴ */}
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-studio-500 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">AI</span>
          </div>
          <h1 className="text-xl font-bold text-gray-800">Data Studio</h1>
        </div>

        {/* プロジェクト選択 */}
        <div className="relative">
          <button
            onClick={() => setShowProjectMenu(!showProjectMenu)}
            className="flex items-center space-x-2 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <FolderOpen size={16} />
            <span className="text-sm font-medium">
              {currentProject ? currentProject.name : "プロジェクトを選択"}
            </span>
            <ChevronDown size={14} />
          </button>

          {/* プロジェクトメニュー */}
          {showProjectMenu && (
            <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
              <div className="p-2">
                {/* 新規プロジェクト */}
                <button
                  onClick={() => {
                    onNewProject();
                    setShowProjectMenu(false);
                  }}
                  className="w-full flex items-center space-x-2 px-3 py-2 text-left hover:bg-gray-50 rounded-md"
                >
                  <Plus size={16} className="text-green-600" />
                  <span className="text-sm font-medium">
                    新しいプロジェクト
                  </span>
                </button>

                {projects.length > 0 && (
                  <>
                    <hr className="my-2" />

                    {/* プロジェクト一覧 */}
                    <div className="max-h-60 overflow-y-auto">
                      {projects.map((project) => (
                        <div
                          key={project.id}
                          className={clsx(
                            "flex items-center justify-between px-3 py-2 rounded-md hover:bg-gray-50",
                            currentProject?.id === project.id && "bg-studio-50"
                          )}
                        >
                          <button
                            onClick={() => handleSwitchProject(project.id)}
                            className="flex-1 text-left"
                          >
                            <div className="flex items-center space-x-2">
                              <FileText size={14} />
                              <div>
                                <div className="text-sm font-medium">
                                  {project.name}
                                </div>
                                <div className="text-xs text-gray-500">
                                  更新:{" "}
                                  {new Date(
                                    project.updatedAt
                                  ).toLocaleDateString("ja-JP")}
                                </div>
                              </div>
                            </div>
                          </button>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteProject(project.id, project.name);
                            }}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            title="削除"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 右側: アクション */}
      <div className="flex items-center space-x-3">
        {/* 実行状況 */}
        {hasRunningJobs && (
          <button
            onClick={onToggleExecution}
            className="flex items-center space-x-2 px-3 py-2 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
          >
            <Activity size={16} className="animate-pulse" />
            <span className="text-sm font-medium">実行中</span>
          </button>
        )}

        {/* 保存 */}
        {currentProject && (
          <button
            onClick={handleSave}
            className="flex items-center space-x-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            title="保存 (Ctrl+S)"
          >
            <Save size={16} />
            <span className="text-sm">保存</span>
          </button>
        )}

        {/* 設定 */}
        <button
          className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
          title="設定"
        >
          <Settings size={16} />
        </button>

        {onLogout && (
          <button
            onClick={onLogout}
            className="px-3 py-2 text-sm text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
          >
            ログアウト
          </button>
        )}
      </div>

      {/* メニューが開いている時のオーバーレイ */}
      {showProjectMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowProjectMenu(false)}
        />
      )}
    </header>
  );
};

export default Header;
